import React from "react"
import { ExternalLink, MapPin, Phone, Mail } from "lucide-react";
import { useLanguageContext } from "../context/languageContext";
import { telHref } from '../utils/telHref';

export const Footer = () => {
    const { t } = useLanguageContext();
    const contactNumber = '+355 69 205 5470';

    const scrollTo = (id) => (e) => {
        e.preventDefault();
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    };

    const links = [
        { id: 'menu', label: 'Menu' },
        { id: 'gallery', label: 'Gallery' },
        { id: 'private-celebrations', label: 'Events' },
        { id: 'partners', label: 'Our Partner' },
    ];

    return (
        <footer className="bg-gradient-to-b from-espresso-soft to-espresso text-cream pt-10 pb-6 md:pt-16 md:pb-8">
            <div className="container mx-auto px-4 md:px-12">
                {/* Two columns on phones — stacking all four blocks made the
                    footer taller than the viewport on its own. */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-8 md:gap-12 mb-8 md:mb-16">
                    {/* Brand */}
                    <div className="col-span-2 md:col-span-1">
                        <h2 className="font-serif text-xl md:text-2xl font-bold text-gold mb-2 md:mb-6">{t("Pasticeri Cialda")}</h2>
                        <p className="text-gray-400 leading-relaxed text-xs md:text-sm">
                            {t("Krijojmë kujtime të ëmbla. Ëmbëlsira artizanale të përgatitura me pasion.")}
                        </p>
                    </div>

                    {/* Quick Links */}
                    <div>
                        <h3 className="text-gold font-bold uppercase tracking-widest text-xs md:text-sm mb-3 md:mb-6">{t("Discover")}</h3>
                        <ul className="space-y-2 md:space-y-4">
                            {links.map((link) => (
                                <li key={link.id}>
                                    <a
                                        href={`#${link.id}`}
                                        onClick={scrollTo(link.id)}
                                        className="text-gray-400 hover:text-gold transition-colors text-xs md:text-sm"
                                    >
                                        {t(link.label)}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Hours — sits beside Discover on phones */}
                    <div className="md:order-last">
                        <h3 className="text-gold font-bold uppercase tracking-widest text-xs md:text-sm mb-3 md:mb-6">{t("Orari i hapjes")}</h3>
                        <p className="text-xs md:text-sm text-gray-400">{t("E Hënë - E Diel")}</p>
                        <p className="text-xs md:text-sm text-white">6:00 - 23:00</p>
                    </div>

                    {/* Contact */}
                    <div className="col-span-2 md:col-span-1">
                        <h3 className="text-gold font-bold uppercase tracking-widest text-xs md:text-sm mb-3 md:mb-6">{t("Na kontaktoni")}</h3>
                        <ul className="space-y-2 md:space-y-4">
                            <li className="flex items-start gap-2 md:gap-3 text-gray-400 text-xs md:text-sm">
                                <MapPin className="w-4 h-4 md:w-5 md:h-5 text-gold shrink-0 mt-0.5" />
                                <span>{t("Rruga Taulantia")}, {t("Durres")}</span>
                            </li>
                            <li className="flex items-center gap-2 md:gap-3 text-gray-400 text-xs md:text-sm">
                                <Phone className="w-4 h-4 md:w-5 md:h-5 text-gold shrink-0" />
                                <a href={telHref(contactNumber)} className="hover:text-white transition-colors">{contactNumber}</a>
                            </li>
                            <li className="flex items-center gap-2 md:gap-3 text-gray-400 text-xs md:text-sm">
                                <Mail className="w-4 h-4 md:w-5 md:h-5 text-gold shrink-0" />
                                <span>info@cialda.com</span>
                            </li>
                        </ul>

                        {/* Call Button */}
                        <a
                            href={telHref(contactNumber)}
                            className="inline-flex items-center gap-2 mt-4 md:mt-6 px-5 py-2.5 md:px-6 md:py-3 bg-gold text-dark-chocolate font-bold text-sm rounded-xl hover:bg-white transition-colors duration-300"
                        >
                            <Phone className="w-4 h-4" />
                            {t("Call to Order")}
                        </a>
                    </div>
                </div>

                <div className="border-t border-white/10 pt-5 md:pt-8 flex flex-col md:flex-row justify-between items-center gap-3 md:gap-4">
                    <p className="text-gray-400 text-xs">
                        &copy; {new Date().getFullYear()} {t("Pasticeri Cialda")}. {t("Të gjitha të drejtat e rezervuara.")}
                    </p>
                    <a
                        href="https://clickapp.win"
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-2 text-gray-400 hover:text-gold transition-colors text-xs"
                    >
                        <span>Powered by Click App</span>
                        <ExternalLink className="w-3 h-3" />
                    </a>
                </div>
            </div>
        </footer>
    );
};
